import type { Event, GeoJSONFeature, Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';
import type { RefObject } from 'react';
import { registerMapEvent } from '@/libs/utils/registerMapEvents';
import { LayerId, SourceId } from '../constants';

function pointCoordinates(feature: GeoJSON.Feature | GeoJSONFeature): [number, number] | undefined {
  const geometry = feature.geometry;
  return geometry?.type === 'Point' ? (geometry.coordinates as [number, number]) : undefined;
}

export function useClusterInteractions(map: RefObject<MapboxMap | null>) {
  /**
   * Eases to the zoom that breaks the cluster apart. Owned here, not by the deals: the chip marks
   * every cluster, and this path is the one that always runs.
   */
  function handleClusterInteractionClick(e: MapMouseEvent & Event) {
    const currentMap = map.current;
    if (!currentMap) return;
    const [feature] = currentMap.queryRenderedFeatures(e.point, {
      layers: [LayerId.CLUSTER_CHIP],
    });
    if (!feature) return;

    const clusterId = feature.properties?.cluster_id;
    const center = pointCoordinates(feature);
    if (typeof clusterId !== 'number' || !center) return;

    // getClusterExpansionZoom is GeoJSON-only, and calling it blind throws inside the listener.
    const source = currentMap.getSource(SourceId.CLUSTERED_MARKS);
    if (source?.type !== 'geojson') return;

    source.getClusterExpansionZoom(clusterId, (error, zoom) => {
      if (error || typeof zoom !== 'number') return;
      currentMap.easeTo({ center, zoom });
    });
  }

  // Resolves a click on an unclustered pin to a location id + how many pins
  // share its coordinates, and reports both upward. The caller (which owns
  // the actual selection state) decides select vs. deselect and any
  // resulting camera/icon reaction.
  function handleUnclusteredInteractionClick(
    e: (MapMouseEvent & Event) | undefined,
    updateCurrentLocation: (locationId: string, coincidentCount?: number) => void,
    forcedFeatures?: GeoJSON.Feature[] | GeoJSONFeature[], // acceptable: we only read properties + Point coordinates
  ) {
    if (!map.current) return;
    const features =
      forcedFeatures ??
      (e
        ? map.current.queryRenderedFeatures(e.point, {
            // The selected pin lives on its own layer, so a query against the unclustered one
            // alone would lose it — clicking it again could no longer deselect.
            layers: [LayerId.SELECTED_LOCATION_MARK, LayerId.LOCATION_UNCLUSTERED].filter((id) =>
              map.current?.getLayer(id),
            ),
          })
        : []);

    if (!features.length) return;

    // Pick the newest pin (highest sortKey) so stacked pins resolve deterministically.
    const picked = features.reduce((top, f) =>
      Number(f.properties?.sortKey ?? 0) > Number(top.properties?.sortKey ?? 0) ? f : top,
    );
    const locationId = picked.properties?.id;

    // Count pins at the same coordinates for the detail card warning.
    const pickedCoords = pointCoordinates(picked);
    const coincidentCount = pickedCoords
      ? features.filter((f) => {
          const coords = pointCoordinates(f);
          return coords?.[0] === pickedCoords[0] && coords?.[1] === pickedCoords[1];
        }).length
      : 1;

    updateCurrentLocation(locationId, coincidentCount);
  }

  function setupClusterInteractions(
    updateCurrentLocation: (locationId: string, coincidentCount?: number) => void,
  ) {
    if (!map.current) return () => {};
    const cleanupClusterClick = registerMapEvent(
      map.current,
      'click',
      LayerId.CLUSTER_CHIP,
      handleClusterInteractionClick,
    );
    const cleanupMarkerClick = registerMapEvent(
      map.current,
      'click',
      LayerId.LOCATION_UNCLUSTERED,
      (e: MapMouseEvent & Event) => handleUnclusteredInteractionClick(e, updateCurrentLocation),
    );
    // The selected pin is drawn from its own layer, and these listeners are layer-scoped — without
    // its own registration a click on the open pin would not fire at all.
    const cleanupSelectedClick = registerMapEvent(
      map.current,
      'click',
      LayerId.SELECTED_LOCATION_MARK,
      (e: MapMouseEvent & Event) => handleUnclusteredInteractionClick(e, updateCurrentLocation),
    );

    return () => {
      cleanupClusterClick();
      cleanupMarkerClick();
      cleanupSelectedClick();
    };
  }

  return { setupClusterInteractions, handleUnclusteredInteractionClick };
}
